import * as React from 'react'
import { Suspense, useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '@intellimon/shared'
import {
  Sidebar, SidebarItem, Header, HeaderUser, NotificationCenter, Logo,
  ConnectionStatus, BreadcrumbItem,
} from '@intellimon/shared/components'
import { useAuthStore } from '../../stores/authStore'
import { useBreadcrumbStore } from '../../stores/breadcrumbStore'
import { useTenantSettings } from '../../hooks/useTenantSettings'
import { useTenantInfo } from '../../hooks/useTenantInfo'
import { useUiStore } from '../../stores/uiStore'
import { useNotifications, useUnreadCount, useMarkAsRead, useMarkAllAsRead } from '../../hooks/useNotifications'
import { useRealtimeUpdates } from '../../hooks/useRealtimeUpdates'
import {
  LayoutDashboard, MapPin, Settings, Wifi, Radio, Users, User, Shield, Database,
  type LucideIcon,
} from 'lucide-react'

const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard, MapPin, Settings, Wifi, Radio, Users, User, Shield, Database,
}

export interface NavItem {
  title: string
  href: string
  icon: string
  adminOnly?: boolean
}

export interface AppLayoutProps {
  navItems: NavItem[]
  className?: string
}

export function AppLayout({ navItems, className }: AppLayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation('common')

  // Get state from stores
  const { user, tenantSlug, logout } = useAuthStore()
  const { isDarkMode, sidebarCollapsed, toggleDarkMode, toggleSidebar } = useUiStore()
  const detailLabel = useBreadcrumbStore((s) => s.detailLabel)

  const handleToggleSidebar = () => {
    toggleSidebar()
  }

  const handleProfileClick = () => {
    navigate('/profile')
  }

  // Real-time WebSocket updates
  const { status: wsStatus } = useRealtimeUpdates()

  // Tenant info for company name
  const { data: tenantInfo } = useTenantInfo()

  // Notifications
  const { data: notifData } = useNotifications({ page_size: 10 })
  const { data: unreadData } = useUnreadCount()
  const markReadMutation = useMarkAsRead()
  const markAllReadMutation = useMarkAllAsRead()

  // Convert nav items to sidebar items
  const sidebarItems: SidebarItem[] = navItems
    .filter((item) => !item.adminOnly || user?.role === 'admin')
    .map((item) => {
      const IconComponent = iconMap[item.icon]
      return {
        name: t(item.title, { defaultValue: item.title.split('.').pop() }),
        path: item.href,
        icon: IconComponent ? <IconComponent className="h-5 w-5" /> : null,
      }
    })

  const { data: tenantSettings } = useTenantSettings()
  const logoUrl = tenantSettings?.branding?.logo_url

  const logo = <Logo size="md" src={logoUrl} />

  // Build breadcrumbs from current path
  const breadcrumbs = useMemo((): BreadcrumbItem[] => {
    const segmentLabelMap: Record<string, string> = {
      'sites': t('nav.sites'),
      'mills': t('nav.mills'),
      'lifter-bars': t('nav.lifterBars'),
      'coordinators': t('nav.coordinators'),
      'sensors': t('nav.sensors'),
      'raw-readings': t('nav.rawReadings'),
      'users': t('nav.users'),
      'roles': t('nav.roles'),
      'settings': t('nav.settings'),
      'profile': t('actions.profile'),
    }

    const pathSegments = location.pathname.split('/').filter(Boolean)
    const items: BreadcrumbItem[] = [
      { label: t('nav.dashboard'), href: '/' },
    ]

    let currentPath = ''
    let hasDynamicSegment = false
    for (const segment of pathSegments) {
      currentPath += `/${segment}`
      const label = segmentLabelMap[segment]
      if (label) {
        items.push({ label, href: currentPath })
      } else {
        hasDynamicSegment = true
      }
    }

    // Append detail page label from child pages
    if (hasDynamicSegment && detailLabel) {
      items.push({ label: detailLabel })
    }

    return items
  }, [location.pathname, t, detailLabel])

  return (
    <div className={cn('min-h-screen bg-background', className)}>
      <Sidebar
        items={sidebarItems}
        collapsed={sidebarCollapsed}
        onCollapse={handleToggleSidebar}
        currentPath={location.pathname}
        onNavigate={navigate}
        logo={logo}
        title="IntelliMon"
      />
      <Header
        user={user || undefined}
        tenantName={tenantInfo?.name}
        breadcrumbs={breadcrumbs}
        onNavigate={navigate}
        isDarkMode={isDarkMode}
        onToggleDarkMode={toggleDarkMode}
        onProfileClick={handleProfileClick}
        onLogout={logout}
        sidebarCollapsed={sidebarCollapsed}
        labels={{
          profile: t('actions.profile'),
          logout: t('actions.logOut'),
        }}
        extraActions={
          <>
            <ConnectionStatus status={wsStatus} />
            <NotificationCenter
              notifications={notifData?.items || []}
              unreadCount={unreadData?.unread_count || 0}
              onMarkAsRead={(id) => markReadMutation.mutate(id)}
              onMarkAllAsRead={() => markAllReadMutation.mutate()}
            />
          </>
        }
      />
      <main
        className={cn(
          'pt-16 min-h-screen transition-all duration-300',
          sidebarCollapsed ? 'pl-16' : 'pl-64'
        )}
      >
        <div className="p-6">
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          }>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  )
}
